import React, { useState } from 'react';
import { useCloseClassMutation } from '../../store/api/localApi';
import { dublinDateString } from '../../utils/date';

/**
 * Close-class dialog (§5.2). The admin picks a last day (default today) and may
 * opt into notifying parents. Close ends the current term at that day and drops
 * untouched future terms; a future term with recorded data blocks the close and
 * its name is listed here.
 */
const CloseClassDialog: React.FC<{
  classId: string;
  className: string;
  onClose: () => void;
  onClosed: () => void;
}> = ({ classId, className, onClose, onClosed }) => {
  const [closeClass, { isLoading }] = useCloseClassMutation();
  const [lastDay, setLastDay] = useState(dublinDateString(new Date()));
  const [notifyParents, setNotifyParents] = useState(false);
  const [error, setError] = useState('');
  const [blockers, setBlockers] = useState<string[]>([]);

  const submit = async () => {
    setError('');
    setBlockers([]);
    try {
      await closeClass({ classId, lastDay, notifyParents }).unwrap();
      onClosed();
    } catch (err: any) {
      const data = err?.data;
      // A future term with recorded data blocks the close (list the terms)...
      if (Array.isArray(data?.blockingTerms)) {
        setBlockers(data.blockingTerms.map((t: { name: string }) => t.name));
        setError(data.message || 'A future term has recorded data.');
        return;
      }
      // ...and a backdated last day that would orphan a not-yet-started child
      // blocks it too (list the children), same as the term-shrink guard.
      if (Array.isArray(data?.affectedChildren)) {
        setBlockers(data.affectedChildren.map((c: { studentName: string }) => c.studentName));
        setError(data.message || 'Some enrolled children start after this last day.');
        return;
      }
      setError(data?.message || data?.error || 'Failed to close the class.');
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" data-testid="close-class-dialog">
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={onClose} />
        <div className="relative z-10 bg-white rounded-lg shadow-xl w-full max-w-md p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-2">Close {className}</h3>
          <p className="text-sm text-gray-500 mb-4">
            The class stops running after its last day. History stays intact; it can be reopened
            later by adding a new term.
          </p>

          {error && (
            <div
              className="rounded-md bg-red-50 border border-red-200 p-3 mb-4"
              data-testid="close-class-error"
            >
              <p className="text-sm text-red-700">{error}</p>
              {blockers.length > 0 && (
                <ul
                  className="mt-2 list-disc list-inside text-sm text-red-700"
                  data-testid="close-class-blockers"
                >
                  {blockers.map((b) => (
                    <li key={b}>{b}</li>
                  ))}
                </ul>
              )}
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Last day</label>
              <input
                type="date"
                data-testid="class-close-date"
                value={lastDay}
                onChange={(e) => setLastDay(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              />
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                data-testid="class-close-notify"
                checked={notifyParents}
                onChange={(e) => setNotifyParents(e.target.checked)}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              Notify parents
            </label>
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <button
              type="button"
              data-testid="close-class-cancel"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 bg-white hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              data-testid="class-close-confirm"
              disabled={isLoading}
              onClick={submit}
              className="px-4 py-2 border border-transparent rounded-md text-sm text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
            >
              {isLoading ? 'Closing…' : 'Close class'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CloseClassDialog;
